import { base58 } from "@scure/base"
import { expectedRpIdForHostname } from "@src/shared/utils/webauthnAssertion"
import { logger } from "@src/utils/logger"

const PASSKEY_NOT_FOUND_MESSAGE =
  "We couldn't detect a Passkey available on your device. Please contact support if you need assistance."

export type WebauthnCredential = {
  publicKey: string
  rawId: string
  hostname?: string
}

export type WebauthnPrfResult = {
  enabled?: boolean
  first?: Uint8Array
  second?: Uint8Array
}

export type WebauthnCredentialWithPrf = WebauthnCredential & {
  prf?: WebauthnPrfResult
}

type CreateNewOptions = {
  prf?: Uint8Array
  pubKeyCredParams?: PublicKeyCredentialParameters[]
  userName?: string
  signal?: AbortSignal
}

type SignMessageWithPrfResult = {
  response: AuthenticatorAssertionResponse
  prf?: Uint8Array
}

type SignMessageCredential = Pick<WebauthnCredential, "rawId" | "hostname">
export type SignMessageOptions = {
  signal?: AbortSignal
  userVerification?: UserVerificationRequirement
}

type WebauthnClientExtensionResults = {
  prf?: {
    enabled?: boolean
    results?: {
      first?: ArrayBuffer | Uint8Array
      second?: ArrayBuffer | Uint8Array
    }
  }
}

export async function signIn(): Promise<string> {
  const rpId = getRelayingPartyId()

  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        rpId,
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [],
        timeout: 60000,
      },
    })

    /**
     * Some providers may return a plain object (e.g. 1Password),
     * other can return a PublicKeyCredential instance (e.g. iCloud Keychain).
     *
     * All in all, the interface of `assertion` matches PublicKeyCredential,
     * so we can safely cast it.
     */
    if (assertion == null || assertion.type !== "public-key") {
      throw new Error(PASSKEY_NOT_FOUND_MESSAGE)
    }
    const credential = assertion as PublicKeyCredential

    return base58.encode(new Uint8Array(credential.rawId))
  } catch (error) {
    if (isWebAuthnCancellationError(error)) {
      throw error
    }
    logger.info(`No passkey found for rpId: ${rpId}`)
    throw new Error(PASSKEY_NOT_FOUND_MESSAGE)
  }
}

function isWebAuthnCancellationError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false
  return error.name === "AbortError" || error.name === "NotAllowedError"
}

export async function createNew(
  passkeyName?: string
): Promise<WebauthnCredential>
export async function createNew(
  passkeyName: string | undefined,
  options: CreateNewOptions
): Promise<WebauthnCredentialWithPrf>
export async function createNew(
  passkeyName: string | undefined,
  options: CreateNewOptions & { prf: Uint8Array }
): Promise<WebauthnCredentialWithPrf>
export async function createNew(
  passkeyName?: string,
  options?: CreateNewOptions
): Promise<WebauthnCredentialWithPrf> {
  const formattedDate = new Date().toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const finalPasskeyName =
    passkeyName == null || passkeyName === ""
      ? `User ${formattedDate}`
      : passkeyName
  const finalUserName = options?.userName ?? finalPasskeyName

  const registrationOptions: CredentialCreationOptions = {
    signal: options?.signal,
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: {
        name: "near.com",
        id: getRelayingPartyId(),
      },
      user: {
        id: crypto.getRandomValues(new Uint8Array(32)),
        name: finalUserName,
        displayName: finalPasskeyName,
      },
      pubKeyCredParams: [
        ...(options?.pubKeyCredParams ?? [
          { type: "public-key", alg: -8 },
          { type: "public-key", alg: -7 },
        ]),
      ],
      authenticatorSelection: {
        requireResidentKey: true,
        residentKey: "required",
        userVerification: options?.prf != null ? "required" : "preferred",
      },
      timeout: 60000,
      attestation: "direct",
      ...(options?.prf != null
        ? {
            extensions: {
              prf: {
                eval: {
                  first: options.prf,
                },
              },
            },
          }
        : {}),
    },
  }

  const registration = await navigator.credentials.create(registrationOptions)

  /**
   * Some providers may return a plain object (e.g. 1Password),
   * other can return a PublicKeyCredential instance (e.g. iCloud Keychain).
   *
   * All in all, the interface of `registration` matches PublicKeyCredential,
   * so we can safely cast it.
   */
  if (registration == null || registration.type !== "public-key") {
    throw new Error("Invalid attestation type")
  }
  const credential = registration as PublicKeyCredential

  const { publicKey, algorithm } = await extractCredentialPublicKey(credential)

  return {
    rawId: base58.encode(new Uint8Array(credential.rawId)),
    publicKey: formatPublicKey(publicKey, algorithm),
    ...(options?.prf != null
      ? {
          prf: getPrfResult(credential.getClientExtensionResults()),
        }
      : {}),
  }
}

export async function discoverExisting(options: {
  prf: Uint8Array
  signal?: AbortSignal
}): Promise<{
  rawId: string
  prf?: Uint8Array
}> {
  const rpId = getRelayingPartyId()

  try {
    const assertion = await navigator.credentials.get({
      signal: options.signal,
      publicKey: {
        rpId,
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [],
        timeout: 60000,
        userVerification: "required",
        extensions: {
          prf: {
            eval: {
              first: options.prf,
            },
          },
        },
      },
    })

    if (assertion == null || assertion.type !== "public-key") {
      throw new Error(PASSKEY_NOT_FOUND_MESSAGE)
    }

    const credential = assertion as PublicKeyCredential
    const prf = getPrfResult(credential.getClientExtensionResults())

    return {
      rawId: base58.encode(new Uint8Array(credential.rawId)),
      prf: prf?.first,
    }
  } catch (error) {
    if (isWebAuthnCancellationError(error)) {
      throw error
    }
    logger.info(`No passkey found for rpId: ${rpId}`)
    throw new Error(PASSKEY_NOT_FOUND_MESSAGE)
  }
}

export function getRelayingPartyId(
  hostname = window.location.hostname
): string {
  return expectedRpIdForHostname(hostname)
}

export async function signMessage(
  challenge: Uint8Array,
  credential_: SignMessageCredential,
  options?: SignMessageOptions
): Promise<AuthenticatorAssertionResponse>
export async function signMessage(
  challenge: Uint8Array,
  credential_: SignMessageCredential,
  options: { prf: Uint8Array; signal?: AbortSignal }
): Promise<SignMessageWithPrfResult>
export async function signMessage(
  challenge: Uint8Array,
  credential_: SignMessageCredential,
  options?: {
    prf?: Uint8Array
    signal?: AbortSignal
    userVerification?: UserVerificationRequirement
  }
): Promise<AuthenticatorAssertionResponse | SignMessageWithPrfResult> {
  // Use the credential's original hostname for cross-domain passkey support (ROR)
  const rpId = credential_.hostname ?? getRelayingPartyId()
  const assertion = await navigator.credentials.get({
    signal: options?.signal,
    publicKey: {
      rpId,
      challenge,
      allowCredentials: [
        {
          id: base58.decode(credential_.rawId),
          type: "public-key",
        },
      ],
      timeout: 60000,
      ...(options?.userVerification != null
        ? { userVerification: options.userVerification }
        : {}),
      ...(options?.prf != null
        ? {
            userVerification: "required" as const,
            extensions: {
              prf: {
                eval: {
                  first: options.prf,
                },
              },
            },
          }
        : {}),
    },
  })

  /**
   * Some providers may return a plain object (e.g. 1Password),
   * other can return a PublicKeyCredential instance (e.g. iCloud Keychain).
   *
   * All in all, the interface of `assertion` matches PublicKeyCredential,
   * so we can safely cast it.
   */
  if (assertion == null || assertion.type !== "public-key") {
    throw new Error("Invalid assertion")
  }
  const credential = assertion as PublicKeyCredential

  const response = credential.response as AuthenticatorAssertionResponse

  if (options?.prf == null) {
    return response
  }

  const prf = getPrfResult(credential.getClientExtensionResults())

  return {
    response,
    prf: prf?.first,
  }
}

function getPrfResult(
  extensionResults: AuthenticationExtensionsClientOutputs
): WebauthnPrfResult | undefined {
  const prf = (extensionResults as WebauthnClientExtensionResults).prf
  if (prf == null) return undefined

  return {
    enabled: prf.enabled,
    first: prf.results?.first ? toUint8Array(prf.results.first) : undefined,
    second: prf.results?.second ? toUint8Array(prf.results.second) : undefined,
  }
}

function toUint8Array(value: ArrayBuffer | Uint8Array): Uint8Array {
  return value instanceof Uint8Array ? value : new Uint8Array(value)
}

async function extractCredentialPublicKey(credential: PublicKeyCredential) {
  return parsePublicKeyWithWebCrypto(
    credential.response as AuthenticatorAttestationResponse
  ).catch(async (err) => {
    logger.error(err)

    // Fallback
    const { parsePublicKeyFromCBOR } = await import("./parsePublicKeyFromCBOR")
    return parsePublicKeyFromCBOR(credential)
  })
}

async function parsePublicKeyWithWebCrypto(
  response: AuthenticatorAttestationResponse
): Promise<{ publicKey: Uint8Array; algorithm: number }> {
  const publicKeyBuffer = response.getPublicKey()
  if (publicKeyBuffer == null) {
    throw new Error("Public key is null")
  }

  const algorithm = response.getPublicKeyAlgorithm()
  switch (algorithm) {
    case -7: // ES256
      return {
        publicKey: await parseECDSAKey(publicKeyBuffer, "P-256"),
        algorithm,
      }
    case -8: // EdDSA (Ed25519)
      return {
        publicKey: await parseEdDSAKey(publicKeyBuffer),
        algorithm,
      }
    default:
      throw new Error(`Unsupported algorithm: ${algorithm}`)
  }
}

async function parseECDSAKey(
  publicKey: ArrayBuffer,
  namedCurve: string
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "spki", // Simple public-key infrastructure
    publicKey,
    { name: "ECDSA", namedCurve },
    true,
    ["verify"]
  )

  const rawKey = await crypto.subtle.exportKey("raw", cryptoKey)

  const rawKeyArray = new Uint8Array(rawKey)
  if (rawKeyArray.length !== 65) {
    throw new Error(
      `Invalid public key size for ECDSA curve, it must be 65 bytes, but got ${rawKeyArray.length} bytes`
    )
  }
  const x = rawKeyArray.slice(1, 33)
  const y = rawKeyArray.slice(33, 65)
  return new Uint8Array([...x, ...y])
}

async function parseEdDSAKey(
  publicKeyBuffer: ArrayBuffer
): Promise<Uint8Array> {
  const rawKeyArray = new Uint8Array(publicKeyBuffer).slice(12)
  if (rawKeyArray.length !== 32) {
    throw new Error(`Invalid Ed25519 public key length: ${rawKeyArray.length}`)
  }
  return rawKeyArray
}

function formatPublicKey(rawPublicKey: Uint8Array, algorithm: number): string {
  switch (algorithm) {
    case -7:
      return `p256:${base58.encode(rawPublicKey)}`
    case -8:
      return `ed25519:${base58.encode(rawPublicKey)}`
    default:
      throw new Error(`Unsupported public key algorithm ${algorithm}`)
  }
}

export function isSupportedByBrowser(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function"
  )
}
